import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { store, logout, setTokenFromRefresh } from "../redux/internal";
import { toast } from "react-toastify";
import { baseURL, authLoginURL } from "../constants/urls";
import { AuthorizeResponse } from "../models/Authorize";

interface RetryConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

type QueueItem = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let refreshPromise: Promise<AuthorizeResponse> | null = null;
let failedQueue: QueueItem[] = [];

const waitForQueuedToken = (): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  });

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else if (token) {
      item.resolve(token);
    }
  });
  failedQueue = [];
};

const performTokenRefresh = (): Promise<AuthorizeResponse> => {
  const currentToken = store.getState().auth.token;

  if (!currentToken?.refresh_token) {
    return Promise.reject(new Error("No refresh token available"));
  }

  const params = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: currentToken.refresh_token,
    ...(currentToken.scope ? { scope: currentToken.scope } : { scope: "auth_app" }),
  });

  return axios.post<AuthorizeResponse>(`${authLoginURL}token`, params).then((response) => response.data);
};

const startRefresh = (): Promise<AuthorizeResponse> => {
  isRefreshing = true;
  refreshPromise = performTokenRefresh()
    .then((newToken) => {
      store.dispatch(setTokenFromRefresh(newToken));
      processQueue(null, newToken.access_token);
      return newToken;
    })
    .catch((error) => {
      processQueue(error, null);
      throw error;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
  return refreshPromise;
};

export const axiosInstance = axios.create({
  baseURL: baseURL,
});

axiosInstance.interceptors.request.use(async (config) => {
  const state = store.getState().auth;
  const token = state.token;

  if (token?.access_token && token.refresh_token) {
    const now = Math.floor(Date.now() / 1000);
    const fiveMinuteThreshold = 5 * 60;

    if (state.decodedToken.exp && state.decodedToken.exp - now < fiveMinuteThreshold) {
      const promise = isRefreshing && refreshPromise !== null ? refreshPromise : startRefresh();
      try {
        await promise;
      } catch {
        // Proactive refresh failed; proceed with the current token —
        // the response interceptor will handle any resulting 401.
      }
    }
  }

  const latestToken = store.getState().auth.token;
  if (latestToken && config.headers) {
    config.headers["Authorization"] = `Bearer ${latestToken.access_token}`;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig;

    if (error?.response?.status === 401 && !originalRequest._retry) {
      // Another refresh is already in-flight — queue this request
      if (isRefreshing) {
        const token = await waitForQueuedToken();
        if (originalRequest.headers) {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
        }
        return axiosInstance(originalRequest);
      }

      originalRequest._retry = true;

      try {
        const newToken = await startRefresh();
        if (originalRequest.headers) {
          originalRequest.headers["Authorization"] = `Bearer ${newToken.access_token}`;
        }
        return axiosInstance(originalRequest);
      } catch {
        toast.error("Your session has expired, please log back in.", { toastId: 401 });
        setTimeout(() => {
          void store.dispatch(logout());
        }, 3000);
        throw error;
      }
    }

    // For non-401 errors show a toast (avoid duplicate toast for 401 already handled above)
    if (error?.response?.status !== 401) {
      toast.error(axios.isAxiosError(error) ? error.message : (error as string));
    }

    throw error;
  },
);
